import React, { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
    RocketLaunchIcon,
    EyeIcon,
    CheckBadgeIcon
} from '@heroicons/react/24/outline';
import { useFirestore } from '../../hooks/useFirestore';
import { getAboutInfo } from '../../firebase/firestore';
import Section from '../common/Section';
import Button from '../common/Button';
import Loading from '../common/Loading';
import hero1 from '../../assets/images/hero-1.png'; // Reuse hero image for consistency
import './AboutPreview.css';

const AboutPreview = () => {
    const { t, i18n } = useTranslation();
    const { data: aboutInfo, loading } = useFirestore(useCallback(() => getAboutInfo(), []));

    const title = aboutInfo
        ? (i18n.language === 'ar' ? aboutInfo.titleAr : aboutInfo.titleEn)
        : t('about.title');

    const description = aboutInfo
        ? (i18n.language === 'ar' ? aboutInfo.descAr : aboutInfo.descEn)
        : t('about.subtitle');

    if (loading) {
        return (
            <Section className="about-preview">
                <Loading message={t('common.loading')} />
            </Section>
        );
    }

    return (
        <Section className="about-preview" id="about">
            <div className="about-preview-container">
                <motion.div
                    className="about-image-wrapper"
                    initial={{ opacity: 0, x: i18n.language === 'ar' ? 50 : -50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                >
                    <div className="about-image-card">
                        <img src={hero1} alt="Sigma Elevators" />
                        <div className="experience-badge">
                            <span className="years">15+</span>
                            <span className="text">{t('about.yearsExp')}</span>
                        </div>
                    </div>
                </motion.div>

                <motion.div
                    className="about-content"
                    initial={{ opacity: 0, x: i18n.language === 'ar' ? -50 : 50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                >
                    <div className="section-label">{t('about.whoWeAre')}</div>
                    <h2>{title}</h2>
                    <div className="accent-line"></div>
                    <p className="about-description">{description}</p>

                    <div className="about-highlights">
                        <div className="highlight-item">
                            <div className="highlight-icon">
                                <RocketLaunchIcon className="icon-inner" />
                            </div>
                            <div className="highlight-text">
                                <h4>{t('about.mission')}</h4>
                                <p>{aboutInfo?.mission ? (i18n.language === 'ar' ? aboutInfo.mission.ar : aboutInfo.mission.en) : ''}</p>
                            </div>
                        </div>
                        <div className="highlight-item">
                            <div className="highlight-icon">
                                <EyeIcon className="icon-inner" />
                            </div>
                            <div className="highlight-text">
                                <h4>{t('about.vision')}</h4>
                                <p>{aboutInfo?.vision ? (i18n.language === 'ar' ? aboutInfo.vision.ar : aboutInfo.vision.en) : ''}</p>
                            </div>
                        </div>
                    </div>

                    <Link to="/about">
                        <Button variant="primary">
                            {t('about.learnMore')}
                            <CheckBadgeIcon className="btn-icon" />
                        </Button>
                    </Link>
                </motion.div>
            </div>
        </Section>
    );
};

export default AboutPreview;
